/**
 * product-recommendations-add-all.js
 *
 * Adds an "Add All to Cart" button to the product-recommendations block.
 * Adds 1x the parent product's currently selected variant + 1x the first
 * available variant of each recommended product, in a single atomic
 * /cart/add.js request, then opens the cart drawer.
 *
 * Loaded only when block.settings.show_add_all_to_cart is enabled.
 */

const STATE_RESET_DELAY = 2500; // ms to show success/error state before reverting
const CART_ADD_ENDPOINT = `${window.Shopify?.routes?.root || '/'}cart/add.js`.replace('//', '/');

/**
 * Finds the currently selected variant id for the parent/main product.
 * Falls back to the server-rendered value captured at page load if the
 * live product form can't be located (e.g. product has no variant picker).
 */
function getParentVariantId(button) {
  const parentProductId = button.getAttribute('data-parent-product-id');
  const fallbackVariantId = button.getAttribute('data-parent-variant-id');

  const candidateForms = document.querySelectorAll('form[action*="/cart/add"]');

  for (const form of candidateForms) {
    // Skip forms that belong to this recommendations block or any quick-add UI —
    // we only want the *main* product form on the page.
    if (form.closest('product-recommendations')) continue;
    if (form.closest('quick-add-modal, [data-quick-add], quick-add-component')) continue;

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput || !idInput.value) continue;

    const scopedProductId =
      form.closest('[data-product-id]')?.getAttribute('data-product-id') || form.dataset.productId;

    // If we can confirm this form is for the parent product, trust its live value.
    if (!scopedProductId || String(scopedProductId) === String(parentProductId)) {
      return idInput.value;
    }
  }

  return fallbackVariantId || null;
}

function getRecommendedItems(wrapper) {
  const script = wrapper.querySelector('script[data-add-all-items]');
  if (!script) return [];

  try {
    const parsed = JSON.parse(script.textContent.trim());
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (error) {
    console.error('[Add All to Cart] Could not parse recommended items JSON:', error);
    return [];
  }
}

function setState(button, state) {
  button.dataset.state = state;

  const label = button.querySelector('[data-add-all-label]');
  const labelKey = `label${state.charAt(0).toUpperCase()}${state.slice(1)}`;
  const labelText = button.dataset[labelKey];
  if (label && labelText) label.textContent = labelText;

  button.disabled = state === 'loading';
}

function announceStatus(wrapper, message, { visible = false } = {}) {
  const status = wrapper.querySelector('[data-add-all-status]');
  if (!status) return;
  status.textContent = message;
  status.classList.toggle('is-visible', visible);
}

const DRAWER_SELECTORS = ['cart-drawer', 'cart-drawer-component', '#cart-drawer', '[data-cart-drawer]'];

function findCartDrawer() {
  for (const selector of DRAWER_SELECTORS) {
    const el = document.querySelector(selector);
    if (el) return el;
  }
  return null;
}

/**
 * Every Shopify Online Store 2.0 section is wrapped in a
 * <div id="shopify-section-{id}"> at render time — this is a platform
 * guarantee, not theme-specific. We use that wrapper to identify the
 * cart drawer's section id, then re-fetch that section's HTML fresh
 * from the server via the Section Rendering API and swap it in. This
 * is what actually fixes stale/empty drawer content: opening the
 * drawer only toggles visibility, it doesn't by itself pull new cart
 * data unless something explicitly re-fetches it.
 */
async function refreshCartDrawerSection() {
  const drawer = findCartDrawer();
  if (!drawer) return null;

  const sectionWrapper = drawer.closest('[id^="shopify-section-"]');
  if (!sectionWrapper) return drawer;

  const sectionId = sectionWrapper.id.replace('shopify-section-', '');

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString());
    if (!response.ok) return drawer;

    const html = await response.text();
    const temp = document.createElement('div');
    temp.innerHTML = html.trim();

    const freshWrapper = temp.querySelector(`#shopify-section-${sectionId}`) || temp.firstElementChild;
    if (!freshWrapper) return drawer;

    sectionWrapper.replaceWith(freshWrapper);
    // The old `drawer` reference is now detached from the DOM — return the new one.
    return findCartDrawer();
  } catch (error) {
    console.warn('[Add All to Cart] Could not refresh cart drawer section, falling back:', error);
    return drawer;
  }
}

/**
 * Opens the cart drawer after refreshing its content. Falls back through
 * a few broadly-compatible strategies so this keeps working even if the
 * drawer's exact API differs from what's assumed here.
 */
async function openCartDrawer(addResponse) {
  const drawer = await refreshCartDrawerSection();

  // Give the theme's own event system a chance to react too (e.g. header cart
  // count bubble, which typically lives in a different section than the drawer).
  try {
    const mod = await import('./theme-events.js');
    if (mod?.CartUpdateEvent) {
      document.dispatchEvent(
        new mod.CartUpdateEvent(addResponse, 'product-recommendations-add-all', { didError: false })
      );
    }
  } catch (error) {
    // theme-events.js not found at this path — fine, fallbacks below cover it.
  }

  ['cart:update', 'cart:updated', 'cart:refresh', 'cart:open', 'theme:cart:open'].forEach((name) => {
    document.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail: { source: 'product-recommendations-add-all', addResponse } })
    );
  });

  if (drawer) {
    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (typeof drawer.show === 'function') {
      drawer.show();
      return;
    }
    drawer.setAttribute('open', '');
    drawer.removeAttribute('hidden');
  }
}

async function handleAddAllClick(button) {
  if (button.dataset.state === 'loading') return;

  const wrapper = button.closest('.product-recommendations__add-all-wrapper');
  if (!wrapper) return;

  const parentVariantId = getParentVariantId(button);
  const recommendedItems = getRecommendedItems(wrapper);

  const items = [];
  if (parentVariantId) items.push({ id: parentVariantId, quantity: 1 });
  recommendedItems.forEach((item) => {
    if (item.variantId) items.push({ id: item.variantId, quantity: 1 });
  });

  const errorMessage = button.dataset.errorMessage || 'Something went wrong. Please try adding each product individually.';

  if (items.length === 0) {
    setState(button, 'error');
    announceStatus(wrapper, errorMessage, { visible: true });
    setTimeout(() => {
      setState(button, 'idle');
      announceStatus(wrapper, '', { visible: false });
    }, STATE_RESET_DELAY);
    return;
  }

  setState(button, 'loading');
  announceStatus(wrapper, 'Adding items to cart…');

  try {
    const response = await fetch(CART_ADD_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || data.message || 'Cart add failed');
    }

    setState(button, 'success');
    announceStatus(wrapper, `Added ${items.length} item${items.length === 1 ? '' : 's'} to your cart.`);

    await openCartDrawer(data);

    setTimeout(() => setState(button, 'idle'), STATE_RESET_DELAY);
  } catch (error) {
    console.error('[Add All to Cart] Failed to add items:', error);
    setState(button, 'error');
    // Shopify's /cart/add.js is atomic for bulk requests: if any single item is
    // unavailable (e.g. sold out between page load and click), none are added.
    announceStatus(wrapper, errorMessage, { visible: true });
    setTimeout(() => {
      setState(button, 'idle');
      announceStatus(wrapper, '', { visible: false });
    }, STATE_RESET_DELAY);
  }
}

// Event delegation — works for recommendation blocks rendered after initial page load too.
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-add-all-to-cart]');
  if (!button) return;
  event.preventDefault();
  handleAddAllClick(button);
});